<template>
<div class="review-grid-wrapper">
  <div class="fund-review-count-bar">
      <div class="action-bar-left" v-if="!isGridLoading">{{ totalRowsNumber }} Total Funds</div>
      <div class="action-bar-right">
        <contextual-menu
          position="bottom-left"
          text="Import"
          :options="importOptions"
           @selected="importItemSelected"
        >
          <mds-button
            variation="icon-only"
            icon="import"
            type="button"
            data-mds-popover="mds-popover"
            aria-haspopup="true"
            aria-expanded="true"
            aria-controls="mds-popover--8810"
            aria-hidden="true"
            @mouseover="$emit('showToolTip', $event, ['Import'], undefined, 'small')"
            @mouseleave="$emit('hideToolTip')"
            slot="clickTrigger"
          >
          </mds-button>
        </contextual-menu>
      </div>
  </div>
  <div class="fund-review-grid-container">
    <div class="review-grid-panel">
    <template v-if="isGridLoading">
     <mds-loader />
    </template>
    <template v-else>
      <template v-if="tableRows.length > 0">
      <mds-table row-hover show-sortable>
        <mds-thead class="sticky-header">
          <mds-th v-for="(header, i) in gridHeaderConfig"
            :key="i"
            :style="header.style"
            :sortable="header.sortable"
            :sorted="header.sorted"
            @mds-th-sort-changed="handleSortEvent(i)"
          >
            {{header.displayText}}
          </mds-th>
        </mds-thead>
        <mds-tbody>
          <template v-for="(row, index) in tableRows">
            <mds-tr type="expandable"
              :key="index"
              :expanded="row.expanded"
              :aria-controls="index+1"
              @click.native="toggleRow(index)"
              @keyup.enter.native="toggleRow(index)"
            >
              <mds-td v-for="(header, i) in gridHeaderConfig"
                :key="i"
                :style="header.style"
              >
                <template v-if="header.fieldName === 'currentStatus'">
                  <StatusIcons
                    :status="[{ name: row[header.fieldName] }]"
                    @showToolTip="showToolTip"
                    @hideToolTip="hideToolTip" />
                </template>
                <template v-else-if="header.fieldName === 'pendingStatus'">
                  <span v-if="row.pendingStatus">
                    <mds-button variation="icon-only" icon="clock-back" type="button"
                      class="grid-icon"
                      @mouseover="$emit('showToolTip', $event, ['Pending'])"
                      @mouseleave="$emit('hideToolTip')"
                    >
                    </mds-button>
                  </span>
                </template>
                <template v-else-if="header.fieldName === 'checkStatus'">
                  <a
                    href="#"
                    class="check-status-link"
                    @click.stop.prevent="openStatusModal(row)"
                  >
                    Check Status
                  </a>
                </template>
                <template v-else-if="header.fieldName === 'reviewStatus'">
                  <ReviewStatusIcons
                    :status="[row[header.fieldName]]"
                    @showToolTip="showToolTip"
                    @hideToolTip="hideToolTip"
                  />
                </template>
                <template v-else-if="header.fieldName === 'lastReviewDate'">
                  {{ getFormattedDate(row[header.fieldName]) }}
                </template>
                <template v-else-if="header.fieldName === 'hasNotes'">
                  <mds-icon
                    v-if="row.hasNotes"
                    name="document"
                    size="medium"
                  />
                </template>
                <template v-else-if="header.fieldName === 'action'">
                  <span class="review-grid-action-icon">
                    <contextual-menu
                      position="bottom-left"
                      text="Manage Fund"
                      :options="getActionChoices(index, row)"
                      @selected="actionItemSelected"
                    >
                      <mds-button variation="icon-only" icon="caret-circle-down" type="button"
                        class="grid-icon"
                        slot="clickTrigger"
                        data-mds-popover="mds-popover"
                        aria-haspopup="true"
                        aria-expanded="true"
                        aria-controls="mds-popover--8809"
                        aria-hidden="true"
                        @mouseover="$emit('showToolTip', $event, ['Manage Fund'], undefined, 'small')"
                        @mouseleave="$emit('hideToolTip')"
                        @click.stop
                    >
                    </mds-button>
                    </contextual-menu>
                  </span>
                </template>
                <template v-else>
                    {{ getColumnValue(row[header.fieldName]) }}
                </template>
              </mds-td>
            </mds-tr>
            <mds-tr type="container"
              :key="'container-' + index"
              :id="index+1"
              v-if="row.expanded"
            >
              <mds-td :colspan="gridHeaderConfig.length">
                <div class="accordion-section-container">
                  <template v-if="row.accordionLoading">
                    <mds-loader class="accordion-center"/>
                  </template>
                  <template v-else-if="showFundReviewPanel(row.accordionData)">
                    <fund-review-panel-accordion
                      :accordionData="row.accordionData"
                      :accordionLoader="row.accordionLoading"
                    />
                  </template>
                  <template v-else>
                    <div class="accordion-datapoint-error accordion-center">
                      {{ loadErrorMessage(row.accordionAPISuccess) }}
                    </div>
                  </template>
                </div>
              </mds-td>
            </mds-tr>
          </template>
        </mds-tbody>
      </mds-table>
      <mds-pagination class="fund-review-pagination" show-items-info show-items-select
        :total-items="totalRowsNumber"
        :pageSizes="gridPageSizeOptions"
        :pageSize="gridPageSize"
        :page="pageNumber"
        @mds-pagination-page-changed="handleTablePageChange($event)"
      >
      </mds-pagination>
      </template>
      <template v-else>
        <p class="title-message">
          No Data Found!
        </p>
      </template>
    </template>
    </div>
  </div>
</div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import axios from 'axios';

import MdsLoader from '@mds/loader';
import StatusIcons from '@/components/atoms/StatusIcons';
import ReviewStatusIcons from '@/components/atoms/ReviewStatusIcons';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import {
  MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd,
} from '@mds/data-table';
import MdsPagination from '@mds/pagination';
import MdsIcon from '@mds/icon';
import ContextualMenu from '@/components/molecules/ContextualMenu';
import * as FundReviewService from '@/services/FundReview';

import FundReviewPanelAccordion from '@/components/molecules/FundReviewPanelAccordion';
import { cloneDeep, isEmpty, debounce } from 'lodash';
import { format } from 'date-fns';

const defaulgridHeaderConfig = require('@/config/fund-review-grid-columns.json');

const DEFAULT_GRID_PAGE = 1;

export default {
  components: {
    StatusIcons,
    ReviewStatusIcons,
    MdsLoader,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsPagination,
    MdsIcon,
    FundReviewPanelAccordion,
    ContextualMenu,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Organisms/FundReviewGrid' }),
  ],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGridLoading: false,
      gridHeaderConfig: cloneDeep(defaulgridHeaderConfig),
      tableRows: [],
      totalRowsNumber: 0,
      gridPageSize: 20,
      gridPageSizeOptions: [20, 30, 50],
      pageNumber: DEFAULT_GRID_PAGE,
      sortOrder: 1, // Default sort order of default sort column
      sortColIndex: 0, // Index of default sort column
      gridRequestController: null,
      accordionRequestController: null,
      importOptions: [
        {
          id: 'frp-import-fund-reviews',
          disabled: false,
          text: 'Bulk Update Review Status',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedClientName: (state) => state.grid.client.name,
      selectedProduct: (state) => state.grid.product.selected,
      selectedTab: (state) => state.grid.tab,
      searchTerm: (state) => state.grid.searchTerm,
      config: (state) => state.system.config,
      sortConfig: (state) => state.grid.sortConfig,
    }),
    filterStatusSize() {
      return this.filters.status.length;
    },
    filterCategoriesSize() {
      return this.filters.categories.length;
    },
    filterAssetsSize() {
      return this.filters.assets.length;
    },
    filterReviewStatusSize() {
      return this.filters.reviewStatus.length;
    },
  },
  created() {
    this.loadPreviousState();
    this.loadGridData();

    this.debouncedSearchFunds = debounce(() => {
      return this.reloadGrid();
    }, 700);
  },
  methods: {
    ...mapActions([
      'setMaintainTabFlag',
      'setSortConfig',
      'setSavedFilter',
    ]),
    getColumnValue(value) {
      return value || '_';
    },
    getFormattedDate(date) {
      return date ? format(date, 'MMM DD, YYYY') : '_';
    },
    getActionChoices(rowIndex, rowData) {
      const options = [
        {
          id: `change-status-${rowIndex}`,
          disabled: false,
          text: 'Update Review/Fund Status',
          data: {
            actionId: 'update-review-status',
            rowIndex,
            rowData,
          },
        },
        {
          id: `add-fund-to-series-${rowIndex}`,
          disabled: false,
          text: 'Note',
          data: {
            actionId: 'add-note',
            rowIndex,
            rowData,
          },
        },
      ];

      return options;
    },
    actionItemSelected(selectedOption) {
      const { fundId } = selectedOption.data.rowData;
      this.setMaintainTabFlag(true);
      if (selectedOption.data.actionId === 'update-review-status') {
        this.goToUpdateReviewStatus(fundId);
      } else {
        this.goToNotes(fundId);
      }
    },
    loadPreviousState() {
      // Erase default sort config and apply saved sort config
      if (this.sortConfig) {
        this.$set(this.gridHeaderConfig[this.sortColIndex], 'sorted', 0);
        this.sortColIndex = this.sortConfig.sortColIndex;
        this.sortOrder = this.sortConfig.sortOrder;
        this.$set(this.gridHeaderConfig[this.sortColIndex], 'sorted', this.sortOrder);
      }
    },
    getQueryParameters() {
      // Client Id and Pagination params
      const pageNo = this.pageNumber - 1;
      let queryParams = `fiduciaryClientId=${this.selectedClient}&pageNo=${pageNo}&pageSize=${this.gridPageSize}`;

      // Sort Config
      const sortColumn = this.gridHeaderConfig[this.sortColIndex].fieldName;
      const sortDirection = this.sortOrder === 1 ? 'ASC' : 'DESC';
      queryParams += `&sortColumn=${sortColumn}&sortDirection=${sortDirection}`;

      // Search
      if (this.searchTerm) {
        const escapedParameters = encodeURIComponent(this.searchTerm);
        queryParams += `&search=${escapedParameters}`;
      }

      // Filters
      if (this.filters.status.length > 0) {
        const escapedParameters = this.filters.status.map((item) => encodeURIComponent(item.toLowerCase()));
        queryParams += `&statusList=${escapedParameters}`;
      }
      if (this.filters.categories.length > 0) {
        const escapedParameters = this.filters.categories.map((item) => encodeURIComponent(item));
        queryParams += `&category=${escapedParameters}`;
      }
      if (this.filters.assets.length > 0) {
        const escapedParameters = this.filters.assets.map((item) => encodeURIComponent(item));
        queryParams += `&assetClassName=${escapedParameters}`;
      }
      if (this.filters.reviewStatus.length > 0) {
        const escapedParameters = this.filters.reviewStatus.map((item) => encodeURIComponent(item));
        queryParams += `&reviewStatusList=${escapedParameters}`;
      }

      return queryParams;
    },
    loadGridData() {
      if (this.gridRequestController) {
        this.gridRequestController.cancel();
      }
      this.isGridLoading = true;
      const queryParams = this.getQueryParameters();
      this.gridRequestController = axios.CancelToken.source();
      FundReviewService.getReviewFunds({
        productId: this.selectedProduct,
        queryParams,
        axiosCancelToken: this.gridRequestController?.token,
      }).then((response) => {
        this.isGridLoading = false;
        if (response.status.success) {
          this.tableRows = response.data.content;
          this.totalRowsNumber = response.data.totalElements;
        }
        this.gridRequestController = null;
      }).catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        this.gridRequestController = null;
        this.handleError(error);
        this.isGridLoading = false;
      });
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.$store.dispatch('openNotification', {
          message: error.message,
          notificationType: 'error',
        });
      }
    },
    toggleRow(index) {
      const status = this.tableRows[index].expanded ? !this.tableRows[index].expanded : true;
      this.$set(this.tableRows[index], 'expanded', status);
      const { expanded, fundId, accordionData } = this.tableRows[index];
      if (expanded && !accordionData) {
        this.tableRows[index].accordionLoading = true;
        this.loadAccordionData(fundId, index);
      }
    },
    handleTablePageChange($event) {
      const { page, pageSize } = $event;
      this.gridPageSize = pageSize;
      this.pageNumber = page;
      this.loadGridData();
    },
    handleSortEvent(columnIndex) {
      // if your tracked sort order is 0 (none), set it 1 (ascending)
      // if there is already an order being tracked inverse it
      this.sortOrder = this.sortOrder === 0 ? 1 : -this.sortOrder;

      // if the table was already sorted by a different column, then reset that column to 0 (none)
      if (this.sortColIndex !== null && this.sortColIndex !== columnIndex) {
        this.$set(this.gridHeaderConfig[this.sortColIndex], 'sorted', 0);
        this.sortOrder = 1;
      }

      // track the current column as the one being sorted on
      this.sortColIndex = columnIndex;
      this.$set(this.gridHeaderConfig[columnIndex], 'sorted', this.sortOrder);

      // Save Sort Configuration
      const sortConfig = {
        sortColIndex: this.sortColIndex,
        sortOrder: this.sortOrder,
      };
      this.setSortConfig(sortConfig);

      this.reloadGrid();
    },
    loadAccordionData(fundId, index) {
      this.accordionRequestController = axios.CancelToken.source();
      FundReviewService.getFundDataPoints({
        fundId,
        axiosCancelToken: this.accordionRequestController?.token,
      }).then((response) => {
        if (response.status.success) {
          this.manageFundDataPointsResponse(index, response);
        }
      }).catch((error) => {
        this.manageFundDataPointsResponse(index);
        const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        }
      }).finally(() => {
        this.accordionRequestController = null;
      });
    },
    manageFundDataPointsResponse(index, res = '') {
      const tableRows = cloneDeep(this.tableRows);
      const tableRow = tableRows[index];
      if (res) {
        tableRow.accordionData = { ...res.data.list[0] };
        tableRow.accordionAPISuccess = true;
      } else {
        tableRow.accordionAPISuccess = false;
      }
      tableRow.accordionLoading = false;
      this.tableRows = tableRows;
    },
    showFundReviewPanel(accordionData) {
      return accordionData && !isEmpty(accordionData);
    },
    loadErrorMessage(success) {
      if (success) {
        return 'The Data points that you are looking for are not available for this fund.';
      }
      /* eslint-disable-next-line max-len */
      return 'Error while Fetching (Loading the data) - There was an issue while fetching the data points, please connect with the system administrator for further assistance.';
    },
    showToolTip(event, message) {
      this.$emit('showToolTip', event, message, undefined, 'small');
    },
    hideToolTip() {
      this.$emit('hideToolTip');
    },
    openStatusModal({
      fundName, secId, clientId, currentStatusId,
    }) {
      this.$store.dispatch('openModal', {
        title: fundName,
        component: 'all-fiduciary-fund-status',
        data: {
          secId,
          clientId,
          currentStatusId,
        },
        size: '900px',
      });
    },
    goToUpdateReviewStatus(fundId) {
      this.$router.push(
        {
          name: 'updateFundReviewStatus',
          params: { fundId },
        },
      );
    },
    goToNotes(fundId) {
      this.$router.push({ name: 'fundReviewNotes', params: { fundId } });
    },
    saveFilter() {
      const filterToSave = cloneDeep(this.filters);
      this.setSavedFilter(filterToSave);
    },
    reloadGrid() {
      if (this.pageNumber === DEFAULT_GRID_PAGE) {
        // If user is on first page then load the Grid
        this.loadGridData();
      } else {
        // Set pageNumber to 1. It also triggers pageChange event which reloads the grid.
        this.pageNumber = DEFAULT_GRID_PAGE;
      }
    },
    importItemSelected() {
      this.setMaintainTabFlag(true);
      this.$router.push({ name: 'importReviews' });
    },
  },
  watch: {
    searchTerm() {
      this.debouncedSearchFunds();
    },
    selectedProduct() {
      this.saveFilter();
      this.reloadGrid();
    },
    filterStatusSize() {
      this.saveFilter();
      this.reloadGrid();
    },
    filterCategoriesSize() {
      this.saveFilter();
      this.reloadGrid();
    },
    filterAssetsSize() {
      this.saveFilter();
      this.reloadGrid();
    },
    filterReviewStatusSize() {
      this.saveFilter();
      this.reloadGrid();
    },
  },
  beforeDestroy() {
    this.gridRequestController && this.gridRequestController.cancel();
    this.accordionRequestController && this.accordionRequestController.cancel();
  },
};
</script>
<style lang="scss">
@import '@mds/link';
.review-grid-wrapper {
    height: calc(100% - 61px);
    overflow-y: auto;
    width: 100%;
    margin-top: $mds-space-4-x;

    .fund-review-count-bar {
      padding: 0 $mds-space-3-x;
      margin-bottom: $mds-space-2-x;

      .action-bar-left {
        @include mds-body-text-m();
        float: left;
      }
      .action-bar-right {
        float: right;
      }
    }
  }
.fund-review-grid-container {
  height: 1px;
  padding: 0 $mds-space-3-x;

  .review-grid-panel {
    padding-bottom: $mds-space-3-x;
    table {
      .check-status-link, .mds-list-group__link:hover {
        color: $mds-color-blue-32;
      }

      // Css to show action popover flowing out of cell
      td:last-child {
        overflow: visible;
      }
    }

    .grid-icon {
      svg {
        @include mds-icon-color($mds-color-neutral-80);
      }
    }
    .review-grid-action-icon {
      .grid-icon {
        width: $mds-space-inset-4-x;
        height: $mds-space-inset-4-x;
        border-radius: 6px;
      }

      &:hover, .grid-icon:hover  {
        background-color: #e0f3fb;
        svg {
          @include mds-icon-color($mds-color-neutral-37);
        }
      }
    }

    .sticky-header {
      th {
        position: sticky;
        top: 0;
        background-color: $mds-color-white;
        z-index: 1;
      }
    }
  }
  .accordion-section-container {
    height: 470px;
    border: 1px solid #ccc;
    border-radius: $mds-space-1-x;
    overflow: auto;
    position: relative;
    margin: $mds-space-1-x 0;
    padding-top:  $mds-space-half-x;
    .accordion-datapoint-error {
      font-size: $mds-font-size-l;
    }
    .accordion-center {
      position: absolute;
      top: 20%;
      left: 50%;
      transform: translateX(-50%);
    }
  }
  .fund-review-pagination {
    margin-top: $mds-space-2-x;
  }
}
</style>
